/**
 * Crop math for the profile-photo dialog — pure, no DOM, so `scripts/check-avatars.mjs` runs it
 * under plain node.
 *
 * The dialog shows a square crop box `box` display-px wide. At zoom 1 the image's SHORT side
 * exactly fills the box ("cover"); zoom multiplies that. The image is positioned by the offset of
 * its top-left corner from the box's top-left (`offX`/`offY`, display px, always <= 0 once
 * clamped, so the box never shows anything outside the image).
 *
 * `completeUpload(fileId, crop: { x, y, size })` wants the square in ORIGINAL image pixels and
 * answers 400 for one that leaves the image — so `cropFromView` rounds to integers and clamps.
 */
export interface CropView {
  /** Original image size (naturalWidth / naturalHeight). */
  natW: number;
  natH: number;
  /** Side of the square crop box, display px. */
  box: number;
  /** >= 1. 1 = short side fills the box. */
  zoom: number;
  offX: number;
  offY: number;
}

export interface CropRect {
  x: number;
  y: number;
  size: number;
}

export const MIN_ZOOM = 1;
export const MAX_ZOOM = 4;

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/** Display px per original px at this zoom. */
export function scaleOf(v: Pick<CropView, 'natW' | 'natH' | 'box' | 'zoom'>): number {
  return (v.box / Math.min(v.natW, v.natH)) * v.zoom;
}

/** The view with zoom in range and the image covering the whole box. */
export function clampView(v: CropView): CropView {
  const zoom = clamp(Number.isFinite(v.zoom) ? v.zoom : MIN_ZOOM, MIN_ZOOM, MAX_ZOOM);
  const s = scaleOf({ ...v, zoom });
  const dispW = v.natW * s;
  const dispH = v.natH * s;
  return {
    ...v,
    zoom,
    offX: clamp(v.offX, v.box - dispW, 0),
    offY: clamp(v.offY, v.box - dispH, 0),
  };
}

/** Zoom 1, image centred in the box. */
export function initialView(natW: number, natH: number, box: number): CropView {
  const s = scaleOf({ natW, natH, box, zoom: 1 });
  return clampView({ natW, natH, box, zoom: 1, offX: (box - natW * s) / 2, offY: (box - natH * s) / 2 });
}

/** Move by (dx, dy) display px. */
export function panView(v: CropView, dx: number, dy: number): CropView {
  return clampView({ ...v, offX: v.offX + dx, offY: v.offY + dy });
}

/** Change zoom keeping the image point under (cx, cy) — box coords, default the centre — fixed. */
export function zoomView(v: CropView, zoom: number, cx = v.box / 2, cy = v.box / 2): CropView {
  const z = clamp(zoom, MIN_ZOOM, MAX_ZOOM);
  const s0 = scaleOf(v);
  const s1 = scaleOf({ ...v, zoom: z });
  const px = (cx - v.offX) / s0;
  const py = (cy - v.offY) / s0;
  return clampView({ ...v, zoom: z, offX: cx - px * s1, offY: cy - py * s1 });
}

/** The visible square, in ORIGINAL pixels: integers, size >= 1, entirely inside the image. */
export function cropFromView(view: CropView): CropRect {
  const v = clampView(view);
  const s = scaleOf(v);
  const maxSide = Math.max(1, Math.floor(Math.min(v.natW, v.natH)));
  const size = clamp(Math.round(v.box / s), 1, maxSide);
  const x = clamp(Math.round(-v.offX / s), 0, Math.max(0, Math.floor(v.natW) - size));
  const y = clamp(Math.round(-v.offY / s), 0, Math.max(0, Math.floor(v.natH) - size));
  return { x, y, size };
}
